import React, { useState, useCallback, useEffect } from 'react';
import { uploadPastPaper, getAllSubjects } from '../../services/admin';
import { uploadFile as uploadToCloudinary } from '../../services/cloudinary';
import '../../styles/bulk-upload.css';

const BulkPaperUpload = ({ onClose, onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [results, setResults] = useState([]);
  const [errors, setErrors] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(true);

  // Fetch subjects from database
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const subjectsData = await getAllSubjects();
        setSubjects(subjectsData || []);
      } catch (error) {
        console.error('Error fetching subjects:', error);
        setSubjects([]);
      } finally {
        setLoadingSubjects(false);
      }
    };

    fetchSubjects();
  }, []);

  const LEVELS = [
    { value: 'grade7', label: 'Grade 7' },
    { value: 'grade12', label: 'Grade 12' }
  ];

  const PAPER_TYPES = [
    { value: 'past_paper', label: 'Past Paper' },
    { value: 'mock_exam', label: 'Mock Exam' },
    { value: 'revision', label: 'Revision Notes' }
  ];

  const TERMS = [
    { value: 'term1', label: 'Term 1' },
    { value: 'term2', label: 'Term 2' },
    { value: 'term3', label: 'Term 3' }
  ];

  const handleFileSelect = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files);
    const pdfFiles = selectedFiles.filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length === 0) {
      alert('Please select only PDF files');
      return;
    }

    const filesWithDefaults = pdfFiles.map(file => ({
      file,
      title: file.name.replace('.pdf', '').replace(/_/g, ' ').replace(/-/g, ' '),
      subject: '',
      level: '',
      paperType: 'past_paper',
      term: '',
      year: new Date().getFullYear(),
      description: ''
    }));

    setFiles(prev => [...prev, ...filesWithDefaults]);
  }, []);

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const updateFileData = (index, field, value) => {
    setFiles(prev => prev.map((fileData, i) => 
      i === index ? { ...fileData, [field]: value } : fileData
    ));
  };

  const uploadPaperFile = async (fileData, index) => {
    try {
      // Upload to Cloudinary using the proper service
      const uploadResult = await uploadToCloudinary(fileData.file, 'past_papers');

      // Create paper data
      const paperData = {
        title: fileData.title,
        description: fileData.description || `${fileData.paperType} for ${fileData.subject}`,
        subjectId: fileData.subject,
        levelId: fileData.level,
        paperType: fileData.paperType,
        term: fileData.term,
        year: parseInt(fileData.year),
        fileUrl: uploadResult.url,
        fileName: uploadResult.publicId,
        fileSize: fileData.file.size,
        fileType: 'pdf',
        isActive: true,
        createdAt: new Date()
      };

      // Save to Firestore
      await uploadPastPaper(paperData);

      return { success: true, fileData: fileData.file.name };
    } catch (error) {
      console.error('Error uploading file:', error);
      return { success: false, fileData: fileData.file.name, error: error.message };
    }
  };

  const handleBulkUpload = async () => {
    if (files.length === 0) {
      alert('Please select files to upload');
      return;
    }

    // Validate all files have required fields
    const invalidFiles = files.filter(f => !f.subject || !f.level || !f.title);
    if (invalidFiles.length > 0) {
      alert('Please fill in all required fields (title, subject, level) for all files');
      return;
    }

    setUploading(true);
    setResults([]);
    setErrors([]);

    const uploadPromises = files.map(async (fileData, index) => {
      setUploadProgress(prev => ({ ...prev, [index]: 'uploading' }));
      
      try {
        const result = await uploadPaperFile(fileData, index);
        setUploadProgress(prev => ({ ...prev, [index]: result.success ? 'success' : 'error' }));
        return result;
      } catch (error) {
        setUploadProgress(prev => ({ ...prev, [index]: 'error' }));
        return { success: false, fileData: fileData.file.name, error: error.message };
      }
    });

    const allResults = await Promise.all(uploadPromises);
    
    const successful = allResults.filter(r => r.success);
    const failed = allResults.filter(r => !r.success);

    setResults(successful);
    setErrors(failed);

    setUploading(false);

    if (successful.length > 0) {
      onUploadComplete && onUploadComplete(successful.length);
      setTimeout(() => {
        onClose();
      }, 2000);
    }
  };

  const applyBulkSettings = () => {
    const commonSubject = document.getElementById('bulkSubject').value;
    const commonLevel = document.getElementById('bulkLevel').value;
    const commonPaperType = document.getElementById('bulkPaperType').value;
    const commonTerm = document.getElementById('bulkTerm').value;
    const commonYear = document.getElementById('bulkYear').value;

    setFiles(prev => prev.map(file => ({
      ...file,
      subject: commonSubject || file.subject,
      level: commonLevel || file.level,
      paperType: commonPaperType || file.paperType,
      term: commonTerm || file.term,
      year: commonYear || file.year
    })));
  };

  return (
    <div className="bulk-upload-modal">
      <div className="bulk-upload-content">
        {loadingSubjects ? (
          <div className="loading-container">
            <div className="loading-spinner">Loading subjects...</div>
          </div>
        ) : (
          <>
            <div className="bulk-upload-header">
              <h2>Bulk Upload Past Papers</h2>
              <button className="close-btn" onClick={onClose}>×</button>
            </div>

        <div className="bulk-settings">
          <h3>Apply to All Files:</h3>
          <div className="bulk-settings-grid">
            <select id="bulkSubject">
              <option value="">Select Subject</option>
              {loadingSubjects ? (
                <option>Loading subjects...</option>
              ) : subjects.length > 0 ? (
                subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>{subject.name}</option>
                ))
              ) : (
                <option>No subjects available</option>
              )}
            </select>
            
            <select id="bulkLevel">
              <option value="">Select Level</option>
              {LEVELS.map(level => (
                <option key={level.value} value={level.value}>{level.label}</option>
              ))}
            </select>
            
            <select id="bulkPaperType">
              {PAPER_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            
            <select id="bulkTerm">
              <option value="">Select Term</option>
              {TERMS.map(term => (
                <option key={term.value} value={term.value}>{term.label}</option>
              ))}
            </select>
            
            <input type="number" id="bulkYear" placeholder="Year" defaultValue={new Date().getFullYear()} />
            
            <button type="button" onClick={applyBulkSettings} className="btn btn-secondary">
              Apply to All
            </button>
          </div>
        </div>

        <div className="file-input-section">
          <input
            type="file"
            id="bulkFileInput"
            multiple
            accept=".pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <label htmlFor="bulkFileInput" className="file-input-label">
            📁 Select PDF Files
          </label>
        </div>

        <div className="files-list">
          {files.map((fileData, index) => (
            <div key={index} className="file-item">
              <div className="file-info">
                <span className="file-name">{fileData.file.name}</span>
                <span className="file-size">({(fileData.file.size / 1024 / 1024).toFixed(2)} MB)</span>
              </div>
              
              <div className="file-form">
                <input
                  type="text"
                  placeholder="Title"
                  value={fileData.title}
                  onChange={(e) => updateFileData(index, 'title', e.target.value)}
                  className="file-input"
                />
                
                <select
                  value={fileData.subject}
                  onChange={(e) => updateFileData(index, 'subject', e.target.value)}
                  className="file-select"
                  required
                >
                  <option value="">Subject*</option>
                  {loadingSubjects ? (
                    <option>Loading...</option>
                  ) : subjects.length > 0 ? (
                    subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>{subject.name}</option>
                    ))
                  ) : (
                    <option>No subjects available</option>
                  )}
                </select>
                
                <select
                  value={fileData.level}
                  onChange={(e) => updateFileData(index, 'level', e.target.value)}
                  className="file-select"
                  required
                >
                  <option value="">Level*</option>
                  {LEVELS.map(level => (
                    <option key={level.value} value={level.value}>{level.label}</option>
                  ))}
                </select>
                
                <select
                  value={fileData.paperType}
                  onChange={(e) => updateFileData(index, 'paperType', e.target.value)}
                  className="file-select"
                >
                  {PAPER_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
                
                <select
                  value={fileData.term}
                  onChange={(e) => updateFileData(index, 'term', e.target.value)}
                  className="file-select"
                >
                  <option value="">Term</option>
                  {TERMS.map(term => (
                    <option key={term.value} value={term.value}>{term.label}</option>
                  ))}
                </select>
                
                <input
                  type="number"
                  placeholder="Year"
                  value={fileData.year}
                  onChange={(e) => updateFileData(index, 'year', e.target.value)}
                  className="file-input"
                  min="2000"
                  max="2030"
                />
                
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="remove-btn"
                  disabled={uploading}
                >
                  Remove
                </button>
                
                {uploadProgress[index] && (
                  <div className={`upload-status ${uploadProgress[index]}`}>
                    {uploadProgress[index] === 'uploading' && '⏳ Uploading...'}
                    {uploadProgress[index] === 'success' && '✅ Success'}
                    {uploadProgress[index] === 'error' && '❌ Error'}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {results.length > 0 && (
          <div className="upload-results">
            <h3>✅ Successfully Uploaded ({results.length})</h3>
            <ul>
              {results.map((result, index) => (
                <li key={index}>{result.fileData}</li>
              ))}
            </ul>
          </div>
        )}

        {errors.length > 0 && (
          <div className="upload-errors">
            <h3>❌ Upload Errors ({errors.length})</h3>
            <ul>
              {errors.map((error, index) => (
                <li key={index}>{error.fileData}: {error.error}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="bulk-upload-actions">
          <button
            type="button"
            onClick={handleBulkUpload}
            className="btn btn-primary"
            disabled={uploading || files.length === 0}
          >
            {uploading ? `Uploading ${files.length} files...` : `Upload ${files.length} Files`}
          </button>
          
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            disabled={uploading}
          >
            Cancel
          </button>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BulkPaperUpload;
